import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const inputCls =
  "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    confirm: "",
    bottle_code: "",
  });
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!terms) {
      setError("Please accept the Terms and Conditions to continue.");
      return;
    }
    setLoading(true);
    const { confirm, ...payload } = form;
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      if (res.pending) {
        toast.success(res.message || "Registration received — pending admin approval.");
        navigate("/login");
      } else {
        toast.success(res.message || "Account activated successfully.");
        navigate("/");
      }
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Installer Registration</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white">Create your account</h1>
      <p className="mt-2 text-sm text-neutral-500">Register with a SGBINOXE bottle code to activate instantly — or without one, and an admin will approve your account.</p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        {error && (
          <div className="rounded-lg border border-[#E30613]/30 bg-[#E30613]/10 px-4 py-3 text-sm text-[#FF6069]" data-testid="register-error">
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-neutral-300">Business Name</Label>
            <Input value={form.business_name} onChange={set("business_name")} required className={inputCls} data-testid="register-business-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-neutral-300">Contact Name</Label>
            <Input value={form.contact_name} onChange={set("contact_name")} required className={inputCls} data-testid="register-contact-name" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-neutral-300">Business Email</Label>
          <Input type="email" value={form.email} onChange={set("email")} required className={inputCls} data-testid="register-email" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-neutral-300">Phone Number</Label>
            <Input value={form.phone} onChange={set("phone")} required className={inputCls} data-testid="register-phone" />
          </div>
          <div className="space-y-2">
            <Label className="text-neutral-300">Business Address</Label>
            <Input value={form.address} onChange={set("address")} required className={inputCls} data-testid="register-address" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-neutral-300">Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} required className={inputCls} data-testid="register-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-neutral-300">Confirm Password</Label>
            <Input type="password" value={form.confirm} onChange={set("confirm")} required className={inputCls} data-testid="register-confirm" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-neutral-300">Bottle Activation Code <span className="text-neutral-600">(optional)</span></Label>
          <Input value={form.bottle_code} onChange={set("bottle_code")} placeholder="01A-0001-K9Q4X" className={`${inputCls} font-mono tracking-wide`} data-testid="register-bottle-code" />
          <p className="text-xs text-neutral-500">Enter a valid unused SGBINOXE bottle code to activate instantly with 10 credits. Leave it blank to register without one — an admin will review and approve your account.</p>
        </div>

        <label className="flex items-start gap-3 pt-1 text-sm text-neutral-400">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 accent-[#E30613]" data-testid="register-terms" />
          <span>I accept the SGBINOXE <span className="text-neutral-200">Terms and Conditions</span>.</span>
        </label>

        <button
          type="submit"
          disabled={loading}
          className="flex h-11 w-full items-center justify-center rounded-lg bg-[#E30613] font-medium text-white transition-colors duration-200 hover:bg-[#FF1C2A] disabled:opacity-60"
          data-testid="register-submit"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
        </button>
      </form>

      <p className="mt-6 text-sm text-neutral-500">
        Already registered?{" "}
        <Link to="/login" className="font-medium text-white transition-colors hover:text-[#FF1C2A]" data-testid="login-link">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
